import { useMutation, useQueryClient } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING, REPRESENTATIVES_MUTATION } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import {
  IRepresentativeHideMediaPayload,
  IRepresentativeSingleProfileHiddenMedia,
  IRepresentativeSingleProfileMedia,
  IRrepresentativeShowHiddenMediaPayload,
  ISingleRepresentativeAttachedMediaPayloadResponse,
  ISingleRepresentativeHiddenMediaPayloadResponse,
} from 'shared/interfaces/IRepresentativesEditProfile';
import { profileDao } from 'shared/dao/profileDao';
import { FileType } from 'shared/enums/FileType';
import { representativesDao } from 'shared/dao/representativesDao';
import { useRef } from 'react';

const { unSelectProfileMedia } = profileDao();
const { postRepresentativeShowHiddenMedia } = representativesDao();

export const useOptimisticHiddenMedia = () => {
  const multipleProfileKey = [REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile];
  const invalidateRef = useRef<boolean>(false);
  const queryClient = useQueryClient();
  const useOptimisticHideMedia = (file_type: FileType, profileId: string) => {
    const mutationKey = [REPRESENTATIVES_MUTATION.hide_attached_media_key, file_type, profileId];

    return useMutation({
      mutationKey,

      mutationFn: async (payload: IRepresentativeHideMediaPayload) => {
        return await unSelectProfileMedia(payload.profileId, payload.mediaId);
      },
      onMutate: async (payload: IRepresentativeHideMediaPayload) => {
        let hiddenNode: IRepresentativeSingleProfileHiddenMedia;
        const attachedMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
          payload.profileId,
          payload.fileType,
        ];
        const hiddenMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
          payload.profileId,
          payload.fileType,
        ];
        const singleProfileQueryKey = [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId];

        await Promise.all([
          queryClient.cancelQueries(attachedMediaQueryKey),
          queryClient.cancelQueries(hiddenMediaQueryKey),
          queryClient.cancelQueries(singleProfileQueryKey),
        ]);

        const previousAttachedMedia: ISingleRepresentativeAttachedMediaPayloadResponse | undefined =
          queryClient.getQueryData(attachedMediaQueryKey);
        const previousHiddenMedia = queryClient.getQueryData(hiddenMediaQueryKey);

        queryClient.setQueryData(attachedMediaQueryKey, (oldData: any) => {
          const prevData: ISingleRepresentativeAttachedMediaPayloadResponse = oldData;
          return {
            ...prevData,
            data: {
              ...prevData.data,
              medium_attachments_connection: {
                ...prevData.data?.medium_attachments_connection,
                nodes: prevData.data?.medium_attachments_connection?.nodes?.filter((node) => {
                  if (node.id !== payload.mediaId) return true;
                  hiddenNode = {
                    id: node?.medium.id || '',
                    file_name: node?.medium?.file_name || '',
                    file_type: node?.medium?.file_type || '',
                    attachment_url: node?.medium?.attachment_url || '',
                    cropped_attachment_url: node?.medium?.cropped_attachment_url,
                    modified_attachment_url: node?.medium?.modified_attachment_url,
                    modified_metadata: node?.medium?.modified_metadata || '',
                  };
                  return false;
                }),
              },
            },
          };
        });

        queryClient.setQueryData(hiddenMediaQueryKey, (oldData: any) => {
          const prevData: ISingleRepresentativeHiddenMediaPayloadResponse = oldData;

          return {
            ...prevData,
            data: {
              ...prevData.data,
              media_connection: {
                ...prevData.data.media_connection,
                nodes: [hiddenNode, ...(prevData.data.media_connection?.nodes || [])],
              },
            },
          };
        });

        return { previousAttachedMedia, previousHiddenMedia };
      },
      onError: (err, payload, context) => {
        // Rollback optimistic update on error
        const attachedMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
          payload.profileId,
          payload.fileType,
        ];
        const hiddenMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
          payload.profileId,
          payload.fileType,
        ];
        queryClient.setQueryData(attachedMediaQueryKey, context?.previousAttachedMedia);
        queryClient.setQueryData(hiddenMediaQueryKey, context?.previousHiddenMedia);
      },
      onSettled: async (data, error, payload) => {
        const singleProfileQueryKey = [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId];
        const isMutating = queryClient.isMutating({ mutationKey }) === 1;
        if (isMutating) {
          if (invalidateRef.current) {
            invalidateRef.current = false;
            queryClient.invalidateQueries([
              REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
              profileId,
              file_type,
            ]);
          }
          queryClient.invalidateQueries(singleProfileQueryKey, {
            refetchInactive: true,
          });
          queryClient.resetQueries(multipleProfileKey);
        }
      },
    });
  };

  const useOptimisticUnHideMedia = (file_type: FileType, profileId: string) => {
    const mutationKey = [REPRESENTATIVES_MUTATION.select_media_key, file_type, profileId];

    return useMutation({
      mutationKey,
      mutationFn: async (payload: IRrepresentativeShowHiddenMediaPayload[]) => {
        return await postRepresentativeShowHiddenMedia(profileId, payload);
      },
      onMutate: async (payload: IRrepresentativeShowHiddenMediaPayload[]) => {
        // The node that will be unhide
        let hiddenNodes: IRepresentativeSingleProfileMedia[] = [];
        // Query Keys
        const attachedMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
          profileId,
          file_type,
        ];
        const hiddenMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
          profileId,
          file_type,
        ];

        // Cancel queries, seen from the docs
        await Promise.all([
          queryClient.cancelQueries(attachedMediaQueryKey),
          queryClient.cancelQueries(hiddenMediaQueryKey),
        ]);

        // Get the previous hidden media data
        const previousHiddenMedia: ISingleRepresentativeHiddenMediaPayloadResponse | undefined =
          queryClient.getQueryData(hiddenMediaQueryKey);

        // Get the previous attached media data
        const previousAttachedMedia = queryClient.getQueryData(attachedMediaQueryKey);

        return { previousAttachedMedia, previousHiddenMedia };
      },
      onError: (err, payload, context) => {
        const attachedMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
          profileId,
          file_type,
        ];
        const hiddenMediaQueryKey = [
          REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
          profileId,
          file_type,
        ];
        queryClient.setQueryData(attachedMediaQueryKey, context?.previousAttachedMedia);
        queryClient.setQueryData(hiddenMediaQueryKey, context?.previousHiddenMedia);
      },
      onSettled: async (data, error, payload) => {
        const hideMutationKey = [REPRESENTATIVES_MUTATION.hide_attached_media_key, file_type, profileId];
        const singleProfileQueryKey = [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId];
        if (
          queryClient.isMutating({ mutationKey }) === 1 &&
          queryClient.isMutating({ mutationKey: hideMutationKey }) === 0
        ) {
          queryClient.invalidateQueries([
            REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
            profileId,
            file_type,
          ]);
          queryClient.invalidateQueries(singleProfileQueryKey, {
            refetchInactive: true,
          });
          queryClient.resetQueries(multipleProfileKey);
        } else {
          invalidateRef.current = true;
        }
      },
    });
  };

  return { useOptimisticHideMedia, useOptimisticUnHideMedia };
};
